/* libs */
import React, { Fragment, Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { asField, Scope } from "informed";
import axios from "axios";

import { SimpleSelectInput, SimpleTextInput, CollectionAccordion, TopicsPicker, ExperiencesPicker } from "./index";
import validatehoc from "../Validator";
import GetItem from "../../../helpers/dataProviders/getItem";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";
import FileManagerUI from "../../ui/FileManager";

const prominenceOptions = [
	{ key: "Primary", value: "Primary", label: "Primary (Displays as H1 text)" },
	{ key: "Secondary", value: "Secondary", label: "Secondary (Displays as H2 text)" },
	{ key: "Tertiary", value: "Tertiary", label: "Tertiary (Displays as H3 text)" },
];

const fontStateOptions = [
	{ key: "Lowlight", value: "Lowlight", label: "Lowlight (Darker Text)" },
	{ key: "Highlight", value: "Highlight", label: "Highlight (Lighter Text)" },
];

class BrushfirePicker extends Component {
	constructor(props) {
		super(props);
		this.appKey = parseAppKey();
		this.state = {
			minimizePlaceholder: false,
			eventData: [],
			eventOptions: [],
			expandedFieldIndex: [],
			expandedFieldTopicIndex: [],
			fieldMetaData: {},
			fieldData: {},
			fieldOptions: {},
			attendeeTypeData: {},
			isLoading: false,
			showAdvanced: false,
			brushfireApiKey: null,
			brushfireAccessKey: null,
			topics: null,
		};

		this.bfClient = null;
	}

	componentDidMount() {
		this.getTopics();
		if (
			this.props.formValues &&
			(this.props.formValues.brushfireApiKey || this.props.formValues.brushfireAccessKey)
		) {
			this.setState(
				{
					brushfireApiKey: this.props.formValues.brushfireApiKey,
					brushfireAccessKey: this.props.formValues.brushfireAccessKey,
				},
				() => {
					this.bfClient = this.generateBrushfireClient();
					this.getBrushfireEvents(true);
				}
			);
		}
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.formValues &&
			(prevProps.formValues.brushfireApiKey !== this.props.formValues.brushfireApiKey ||
				prevProps.formValues.brushfireAccessKey !== this.props.formValues.brushfireAccessKey)
		) {
			this.setState(
				{
					brushfireApiKey: this.props.formValues.brushfireApiKey,
					brushfireAccessKey: this.props.formValues.brushfireAccessKey,
				},
				() => {
					this.bfClient = this.generateBrushfireClient();
					this.getBrushfireEvents();
				}
			);
		}
	}

	generateBrushfireClient = () => {
		let brushfireApiKey = this.state.brushfireApiKey;
		if (brushfireApiKey && brushfireApiKey.match(/^[a-zA-Z0-9]{16}$/)) {
			const authHash = btoa(`${brushfireApiKey}:`);
			return axios.create({
				baseURL: "https://api.brushfire.com/",
				//timeout: 10000,
				headers: {
					Authorization: `Basic ${authHash}`,
					Accept: "application/json",
					"Content-Type": "application/json",
					"api-version": "2019-01-17",
				},
			});
		}
		return null;
	};

	getBrushfireEvents = (buildFields = false) => {
		let bfClient = this.generateBrushfireClient();
		if (bfClient) {
			bfClient
				.get("/events")
				.then(data => {
					let eventData = data.data;
					let eventOptions = [];
					eventData.forEach(event => {
						eventOptions.push({
							key: event.EventNumber,
							value: event.EventNumber,
							label: `${event.Title} (${event.DateInfo})`,
						});
					});
					this.setState({ eventData, eventOptions }, () => {
						if (buildFields) {
							let value = this.props.fieldApi.getValue();
							value.forEach(event => {
								this.getBrushfireFieldMetaDataForEvent(event.eventNumber);
								this.getBrushfireAttendeeTypes(event.eventNumber);
								this.getBrushfireFieldsForEvent(event.eventNumber);
							});
						}
					});
				})
				.catch(error => {});
		}
	};

	getTopics = () => {
		GetItem(`/apps/${this.appKey}/topics`).then(data => {
			let topics = [];
			data.data.forEach(topic => {
				topics.push({
					id: topic.id,
					label: `${topic.name} (${topic.recipientCount})`,
				});
			});
			this.setState({ topics });
		});
	};

	addEvent = e => {
		e.preventDefault();
		e.stopPropagation();
		const { fieldApi } = this.props;
		let value = fieldApi.getValue();
		let newValue;
		if (value) {
			newValue = value.concat();
		} else {
			newValue = [];
		}
		newValue.push({ eventNumber: "", experienceIds: [], eventTitle: "", fields: [], fieldTopics: [] });
		fieldApi.setValue(newValue);
		fieldApi.setTouched(true);
		this.setState({ expandedIndex: newValue.length - 1 });
	};

	addNotificationOption = (e, i) => {
		e.preventDefault();
		e.stopPropagation();
		const { fieldApi } = this.props;
		let value = fieldApi.getValue();
		let newValue = value.concat();

		if (!newValue[i]?.fieldTopics) {
			newValue[i]["fieldTopics"] = [{ topics: [], types: [], options: {}, label: "Item" }];
		} else {
			newValue[i].fieldTopics.push({ topics: [], types: [], options: {}, label: "Item" });
		}

		fieldApi.setValue(newValue);
		fieldApi.setTouched(true);
	};

	addField = (e, i) => {
		e.preventDefault();
		e.stopPropagation();
		const { fieldApi } = this.props;
		let value = fieldApi.getValue();
		let newValue = value.concat();

		newValue[i].fields.push({ field: "", data: "Primary" });
		fieldApi.setValue(newValue);
		fieldApi.setTouched(true);
	};

	changeEvent = (e, i) => {
		let value = e.target.value;
		let { fieldApi } = this.props;
		const { eventData } = this.state;
		const event = eventData.find(evt => evt.EventNumber.toString() === value);
		if (event) {
			let fieldValue = fieldApi.getValue().concat();
			fieldValue[i] = { eventNumber: value, eventTitle: event.Title, fields: [] };
			fieldApi.setValue(fieldValue);
			fieldApi.setTouched(true);
			this.getBrushfireFieldMetaDataForEvent(value);
			this.getBrushfireAttendeeTypes(value);
			this.getBrushfireFieldsForEvent(value);
		}
	};

	changeFieldValue = (e, i, fieldIndex, propertyName) => {
		let value = e.target.value;
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[i].fields[fieldIndex][propertyName] = value;
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	moveFieldUp = (eventIndex, fieldIndex) => {
		const { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		if (fieldIndex > 0) {
			let before = Object.assign({}, fieldValue[eventIndex].fields[fieldIndex - 1]);
			let after = Object.assign({}, fieldValue[eventIndex].fields[fieldIndex]);
			fieldValue[eventIndex].fields[fieldIndex - 1] = after;
			fieldValue[eventIndex].fields[fieldIndex] = before;
			fieldApi.setValue(fieldValue);
			fieldApi.setTouched(true);
		}
	};

	moveFieldDown = (eventIndex, fieldIndex) => {
		const { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		if (fieldIndex < fieldValue[eventIndex].fields.length - 1) {
			let before = Object.assign({}, fieldValue[eventIndex].fields[fieldIndex]);
			let after = Object.assign({}, fieldValue[eventIndex].fields[fieldIndex + 1]);
			fieldValue[eventIndex].fields[fieldIndex] = after;
			fieldValue[eventIndex].fields[fieldIndex + 1] = before;
			fieldApi.setValue(fieldValue);
			fieldApi.setTouched(true);
		}
	};

	deleteFieldCollection = (eventIndex, fieldIndex, fieldType) => {
		const { fieldApi } = this.props;
		let newValue = fieldApi.getValue().concat();
		newValue[eventIndex][fieldType].splice(fieldIndex, 1);
		fieldApi.setValue(newValue);
		fieldApi.setTouched(true);
	};

	getBrushfireFieldMetaDataForEvent = eventNumber => {
		if (this.bfClient) {
			this.bfClient
				.get(`/events/${eventNumber}/fieldmetadata`)
				.then(data => {
					let fieldMetaData = Object.assign({}, this.state.fieldMetaData);
					let fieldOptions = Object.assign({}, this.state.fieldOptions);
					fieldMetaData[eventNumber] = data.data;
					let newFieldOptions = [];
					data.data.forEach(field => {
						newFieldOptions.push({
							key: field.FieldName,
							value: field.FieldName,
							label: field.FieldName,
							group: field.FieldType === "System" ? "System" : field.Type,
						});
					});
					fieldOptions[eventNumber] = newFieldOptions;
					this.setState({ fieldMetaData, fieldOptions });
				})
				.catch(error => {});
		}
	};

	getBrushfireFieldsForEvent = eventNumber => {
		if (this.bfClient) {
			this.bfClient
				.get(`/events/${eventNumber}/fields?fieldType=Attendees&accessKey=${this.state.brushfireAccessKey}`)
				.then(data => {
					let fieldData = Object.assign({}, this.state.fieldData);
					fieldData[eventNumber] = data.data;
					this.setState({ fieldData });
				})
				.catch(error => {});
		}
	};

	getBrushfireAttendeeTypes = eventNumber => {
		if (this.bfClient) {
			this.bfClient
				.get(`/events/${eventNumber}/types?accessKey=${this.state.brushfireAccessKey}`)
				.then(typeData => {
					let attendeeTypeData = Object.assign({}, this.state.attendeeTypeData);
					attendeeTypeData[eventNumber] = typeData.data;
					this.setState({ attendeeTypeData });
				})
				.catch(error => {});
		}
	};

	deleteCollection = index => {
		const { fieldApi } = this.props;
		let newValue = fieldApi.getValue().concat();
		newValue.splice(index, 1);
		fieldApi.setValue(newValue);
		fieldApi.setTouched(true);
	};

	addTopic = (tag, index) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		let addedTagValue = "";
		if (tag.id) {
			addedTagValue = tag.id;
		} else {
			addedTagValue = tag.name;
		}
		if (fieldValue[index].topics) {
			fieldValue[index].topics.push(addedTagValue);
		} else {
			fieldValue[index].topics = [addedTagValue];
		}

		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	removeTopic = (tagIndex, index) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[index].topics.splice(tagIndex, 1);
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	addFieldTopic = (tag, eventIndex, fieldTopicIndex) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		let addedTagValue = "";
		if (tag.id) {
			addedTagValue = tag.id;
		} else {
			addedTagValue = tag.name;
		}

		if (fieldValue[eventIndex].fieldTopics[fieldTopicIndex].topics) {
			fieldValue[eventIndex].fieldTopics[fieldTopicIndex].topics.push(addedTagValue);
		} else {
			fieldValue[eventIndex].fieldTopics[fieldTopicIndex].topics = [addedTagValue];
		}

		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	removeFieldTopic = (tagIndex, eventIndex, fieldTopicIndex) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[eventIndex].fieldTopics[fieldTopicIndex].topics.splice(tagIndex, 1);
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	addExperience = (tag, index) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		let addedTagValue = "";
		if (tag.id) {
			addedTagValue = tag.id;
		} else {
			addedTagValue = tag.name;
		}
		if (fieldValue[index].experienceIds) {
			fieldValue[index].experienceIds.push(addedTagValue);
		} else {
			fieldValue[index].experienceIds = [addedTagValue];
		}

		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	removeExperience = (tagIndex, index) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		const removedExperienceId = fieldValue[index].experienceIds[tagIndex];
		fieldValue[index].experienceIds.splice(tagIndex, 1);
		if (
			fieldValue[index].experienceAttendeeTypes &&
			fieldValue[index].experienceAttendeeTypes[removedExperienceId]
		) {
			delete fieldValue[index].experienceAttendeeTypes[removedExperienceId];
		}
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	changeFontState = (e, i) => {
		let value = e.target.value;
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[i].fontState = value;
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	uploadError = error => {
		this.props.fieldApi.setError(error);
	};

	uploadSuccess = (url, i) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[i].ticketBackground = url;
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	removeFile = (event, i) => {
		event.preventDefault();
		event.stopPropagation();
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[i].ticketBackground = null;
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	toggleExperienceAttendeeType = (eventIndex, experienceId, currentState, attendeeTypeId) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();

		if (!fieldValue[eventIndex].experienceAttendeeTypes) {
			fieldValue[eventIndex].experienceAttendeeTypes = {};
		}
		if (!fieldValue[eventIndex].experienceAttendeeTypes[experienceId]) {
			fieldValue[eventIndex].experienceAttendeeTypes[experienceId] = [];
		}

		if (currentState) {
			const index = fieldValue[eventIndex].experienceAttendeeTypes[experienceId].indexOf(attendeeTypeId);
			if (index !== -1) fieldValue[eventIndex].experienceAttendeeTypes[experienceId].splice(index, 1);
		} else {
			fieldValue[eventIndex].experienceAttendeeTypes[experienceId].push(attendeeTypeId);
		}

		// An empty list is read by the mobile app as "no attendee types allowed", so remove the key instead
		if (fieldValue[eventIndex].experienceAttendeeTypes[experienceId].length === 0) {
			delete fieldValue[eventIndex].experienceAttendeeTypes[experienceId];
		}
		if (Object.keys(fieldValue[eventIndex].experienceAttendeeTypes).length === 0) {
			delete fieldValue[eventIndex].experienceAttendeeTypes;
		}

		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	toggleData = (eventIndex, fieldTopicIndex, currentState, value, propertyName) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		if (currentState) {
			var index = fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName].indexOf(value);
			if (index !== -1) fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName].splice(index, 1);
		} else {
			fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName].push(value);
		}
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	toggleOptionData = (eventIndex, fieldTopicIndex, currentState, optionId, propertyName, fieldItemId) => {
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();

		if (currentState) {
			var index = fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId].indexOf(
				optionId
			);
			if (index !== -1) {
				fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId].splice(index, 1);
				if (fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId].length === 0) {
					delete fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId];
				}
			}
		} else {
			if (!fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId]) {
				fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId] = [];
			}
			fieldValue[eventIndex].fieldTopics[fieldTopicIndex][propertyName][fieldItemId].push(optionId);
		}

		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	changeNotificationLabel = (e, eventIndex, fieldTopicIndex) => {
		let value = e.target.value;
		let { fieldApi } = this.props;
		let fieldValue = fieldApi.getValue().concat();
		fieldValue[eventIndex].fieldTopics[fieldTopicIndex].label = value;
		fieldApi.setValue(fieldValue);
		fieldApi.setTouched(true);
	};

	render() {
		const { fieldState, placeholder, isRequired } = this.props;
		const { value } = fieldState;
		const {
			eventOptions,
			brushfireApiKey,
			fieldMetaData,
			fieldOptions,
			topics,
			attendeeTypeData,
			fieldData,
		} = this.state;
		const { experiences } = this.props.experiences;

		return (
			<Fragment>
				{brushfireApiKey ? (
					<div>
						<div>
							<div className="btn-circle m-right" title="Add An Event" onClick={e => this.addEvent(e)}>
								<i className="fal fa-plus icon"></i>
							</div>
							<h4 className="collection-title">
								{isRequired && <span className="isrequired-label">*</span>}
								{placeholder}
							</h4>
						</div>
						<div className="collection-group">
							{value &&
								value.length > 0 &&
								value.map((event, i) => (
									<CollectionAccordion
										key={i}
										title={event.eventTitle ? event.eventTitle : "Pick an Event"}
										expanded={this.state.expandedIndex === i}
										index={i}
										toggleCollectionItem={expandedIndex => this.setState({ expandedIndex })}
										deleteCollection={this.deleteCollection}
										totalCount={value.length}
									>
										<div className="collection-item-properties">
											{eventOptions && topics && (
												<div>
													<SimpleSelectInput
														id="eventNumber"
														placeholder="Event"
														values={eventOptions}
														value={event.eventNumber}
														onChange={e => {
															this.changeEvent(e, i);
														}}
													/>
													{experiences && experiences.length > 0 && (
														<ExperiencesPicker
															id="experienceIds"
															field="experienceIds"
															placeholder="Experiences"
															helperText="Users can connect their tickets from the event above to these experiences"
															isSimpleInput
															addTag={value => this.addExperience(value, i)}
															removeTag={value => this.removeExperience(value, i)}
															simpleValue={event.experienceIds}
														/>
													)}
													{event.experienceIds?.length > 0 &&
														attendeeTypeData[event.eventNumber] && (
															<div style={{ marginBottom: 16 }}>
																<h4 className="collection-title">
																	Restrict Attendee Types by Experience
																</h4>
																<p className="helper-text">
																	Optionally limit which attendee types can access
																	each experience
																</p>
																{event.experienceIds.map(experienceId => {
																	const experience = experiences.find(
																		exp => exp.id === experienceId
																	);
																	return (
																		<div
																			key={experienceId}
																			className="collection-group"
																		>
																			<h4>{experience?.name || experienceId}</h4>
																			{attendeeTypeData[event.eventNumber].map(
																				attendeeType => {
																					const active = event.experienceAttendeeTypes?.[
																						experienceId
																					]?.includes(attendeeType.Id);
																					return (
																						<div
																							key={attendeeType.Id}
																							className={`on-off-item ${
																								active ? "on" : "off"
																							}`}
																							onClick={() =>
																								this.toggleExperienceAttendeeType(
																									i,
																									experienceId,
																									active,
																									attendeeType.Id
																								)
																							}
																						>
																							<div className="on-off-switch"></div>
																							{attendeeType.Name}
																						</div>
																					);
																				}
																			)}
																		</div>
																	);
																})}
															</div>
														)}
													<SimpleSelectInput
														id="fontState"
														placeholder="Font Class For Ticket Wallet"
														values={fontStateOptions}
														value={event.fontState || "Lowlight"}
														onChange={e => {
															this.changeFontState(e, i);
														}}
													/>
													<TopicsPicker
														id="topics"
														field="topics"
														placeholder="Topics"
														helperText="Users will subscribe to these topics when they identify an attendee from the above event"
														isSimpleInput
														addTag={value => this.addTopic(value, i)}
														removeTag={value => this.removeTopic(value, i)}
														simpleValue={event.topics}
													/>
													<FileManagerUI
														value={event.ticketBackground}
														placeholder="Ticket Background Image"
														uploadError={error => this.uploadError(error)}
														uploadSuccess={url => this.uploadSuccess(url, i)}
														removeFile={e => this.removeFile(e, i)}
													/>
												</div>
											)}
											{fieldMetaData[event.eventNumber] && (
												<div>
													<div>
														<div
															className="btn-circle m-right"
															title="Add A Field"
															onClick={e => this.addField(e, i)}
														>
															<i className="fal fa-plus icon"></i>
														</div>
														<h4 className="collection-title">Fields To Include</h4>
													</div>
													<div className="collection-group">
														{event.fields.map((f, fieldIndex) => (
															<div key={fieldIndex}>
																<CollectionAccordion
																	title={
																		f.field && f.field !== ""
																			? f.field
																			: "Pick a Field"
																	}
																	expanded={
																		this.state.expandedFieldIndex[i] === fieldIndex
																	}
																	index={fieldIndex}
																	toggleCollectionItem={expandedIndex => {
																		let newValue = this.state.expandedFieldIndex.concat();
																		newValue[i] =
																			newValue[i] === expandedIndex
																				? null
																				: expandedIndex;
																		this.setState({ expandedFieldIndex: newValue });
																	}}
																	moveUp={fieldIndex => {
																		this.moveFieldUp(i, fieldIndex);
																	}}
																	moveDown={fieldIndex => {
																		this.moveFieldDown(i, fieldIndex);
																	}}
																	deleteCollection={fieldIndex => {
																		this.deleteFieldCollection(
																			i,
																			fieldIndex,
																			"fields"
																		);
																	}}
																	totalCount={event.fields.length}
																>
																	{fieldOptions[event.eventNumber] && (
																		<div>
																			<SimpleSelectInput
																				value={f.field}
																				field="field"
																				id="field"
																				placeholder="Field"
																				values={fieldOptions[event.eventNumber]}
																				isRequired
																				onChange={e =>
																					this.changeFieldValue(
																						e,
																						i,
																						fieldIndex,
																						"field"
																					)
																				}
																			/>
																			<SimpleSelectInput
																				value={f.data}
																				field="data"
																				id="data"
																				values={prominenceOptions}
																				isRequired
																				onChange={e =>
																					this.changeFieldValue(
																						e,
																						i,
																						fieldIndex,
																						"data"
																					)
																				}
																			/>
																		</div>
																	)}
																</CollectionAccordion>
															</div>
														))}
													</div>
												</div>
											)}

											<div>
												<div>
													<div
														className="btn-circle m-right"
														title="Add a Notification"
														onClick={e => this.addNotificationOption(e, i)}
													>
														<i className="fal fa-plus icon"></i>
													</div>
													<h4 className="collection-title">Notification Options</h4>
												</div>
												<div className="collection-group">
													{event?.fieldTopics?.length > 0 &&
														event.fieldTopics.map((fieldTopic, fieldTopicIndex) => {
															const {
																label,
																topics,
																types: attendeeTypes,
																options: optionIds,
															} = fieldTopic;
															return (
																<CollectionAccordion
																	key={fieldTopicIndex}
																	title={label}
																	expanded={
																		this.state.expandedFieldTopicIndex[
																			fieldTopicIndex
																		] === fieldTopicIndex
																	}
																	index={fieldTopicIndex}
																	toggleCollectionItem={expandedFieldTopicIndex => {
																		let newValue = this.state.expandedFieldTopicIndex.concat();
																		newValue[fieldTopicIndex] =
																			newValue[fieldTopicIndex] ===
																			expandedFieldTopicIndex
																				? null
																				: expandedFieldTopicIndex;
																		this.setState({
																			expandedFieldTopicIndex: newValue,
																		});
																	}}
																	deleteCollection={fieldTopicIndex => {
																		this.deleteFieldCollection(
																			i,
																			fieldTopicIndex,
																			"fieldTopics"
																		);
																	}}
																	totalCount={event.fieldTopics.length}
																>
																	<SimpleTextInput
																		field="Name"
																		id="notifcationOptionName"
																		placeholder="Name"
																		value={label ? label : ""}
																		onChange={e => {
																			this.changeNotificationLabel(
																				e,
																				i,
																				fieldTopicIndex
																			);
																		}}
																	/>
																	<TopicsPicker
																		id="topics"
																		field="topics"
																		placeholder="Topics"
																		helperText="Users will subscribe to these topics when based on Attendee Type and Field Option"
																		isSimpleInput
																		addTag={value =>
																			this.addFieldTopic(
																				value,
																				i,
																				fieldTopicIndex
																			)
																		}
																		removeTag={value =>
																			this.removeFieldTopic(
																				value,
																				i,
																				fieldTopicIndex
																			)
																		}
																		simpleValue={topics}
																	/>
																	<div className="collection-item-properties">
																		{fieldData[event.eventNumber] && (
																			<div>
																				<div className="collection-group">
																					{attendeeTypeData[
																						event.eventNumber
																					] && (
																						<div>
																							<h3 className="ta-center">
																								Attendee Types
																							</h3>

																							{attendeeTypeData[
																								event.eventNumber
																							].map(attendeeType => {
																								let active = attendeeTypes?.includes(
																									attendeeType.Id
																								);
																								return (
																									<div
																										key={
																											attendeeType.Id
																										}
																										className={`on-off-item ${
																											active
																												? "on"
																												: "off"
																										}`}
																										onClick={() =>
																											this.toggleData(
																												i,
																												fieldTopicIndex,
																												active,
																												attendeeType.Id,
																												"types"
																											)
																										}
																									>
																										<div className="on-off-switch"></div>
																										{
																											attendeeType.Name
																										}
																									</div>
																								);
																							})}
																						</div>
																					)}
																					{fieldData[event.eventNumber] && (
																						<div>
																							<h3 className="ta-center">
																								Field Options
																							</h3>
																							{fieldData[
																								event.eventNumber
																							].map(fieldItem => {
																								let jsx = [];
																								if (
																									fieldItem.Options &&
																									fieldItem.Options
																										.length
																								) {
																									jsx.push(
																										<div className="field-group">
																											<h4>
																												{
																													fieldItem.Label
																												}
																											</h4>
																										</div>
																									);
																									fieldItem.Options.forEach(
																										fieldOption => {
																											let active =
																												optionIds[
																													fieldItem
																														.Id
																												] &&
																												optionIds[
																													fieldItem
																														.Id
																												].includes(
																													fieldOption.Id
																												);
																											jsx.push(
																												<div
																													key={
																														fieldOption.Id
																													}
																													className={`on-off-item ${
																														active
																															? "on"
																															: "off"
																													}`}
																													onClick={() =>
																														this.toggleOptionData(
																															i,
																															fieldTopicIndex,
																															active,
																															fieldOption.Id,
																															"options",
																															fieldItem.Id
																														)
																													}
																												>
																													<div className="on-off-switch"></div>
																													{
																														fieldOption.Label
																													}
																												</div>
																											);
																										}
																									);
																									return jsx;
																								} else {
																									return null;
																								}
																							})}
																						</div>
																					)}
																				</div>
																			</div>
																		)}
																	</div>
																</CollectionAccordion>
															);
														})}
												</div>
											</div>
										</div>
									</CollectionAccordion>
								))}
						</div>
					</div>
				) : (
					<div className="alert alert-warning">First, Add Your Brushfire Api Key</div>
				)}
			</Fragment>
		);
	}
}

BrushfirePicker.propTypes = {
	propertyKey: PropTypes.string,
	field: PropTypes.string,
	id: PropTypes.string,
	placeholder: PropTypes.string,
	autocomplete: PropTypes.string,
	values: PropTypes.array,
	isRequired: PropTypes.bool,
	minLength: PropTypes.number,
	maxLength: PropTypes.number,
	validationRegex: PropTypes.string,
	regexMessage: PropTypes.string,
	defaultEnabled: PropTypes.bool,
	fieldState: PropTypes.object,
	appKey: PropTypes.string,
	fieldApi: PropTypes.object,
	formApi: PropTypes.object,
	formValues: PropTypes.object,
	experiences: PropTypes.object,
};

const mapStateToProps = state => ({
	experiences: state.experiences,
});

const Wrapper = validatehoc(
	asField(({ fieldState, ...props }) => <BrushfirePicker fieldState={fieldState} {...props} />),
	"object"
);
export default connect(mapStateToProps)(Wrapper);
